import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';

interface RecommendedActionsProps {
  /**
   * The Security-approved mitigation copy, already resolved and flag-gated by
   * `useAlertDetailModel`. Callers render this component only when non-null.
   */
  mitigation: string;
  /** Rendered as the section heading in the legacy layout; omitted in a card. */
  heading?: string;
  className?: string;
}

/**
 * KIR-4271 — the "Recommended actions" body: the approved mitigation guidance
 * for a mapped `(subPolicyKey, type)` pair.
 *
 * Renders the copy VERBATIM as a single paragraph, which is an acceptance
 * criterion. `data/alert-mitigations.ts` is generated from the approved sheet
 * and its generator rejects any value containing a newline, so there is no
 * multi-paragraph case to handle here — if that ever changes it is a deliberate
 * rendering and v1-contract decision, not a silent collapse.
 */
export function RecommendedActions({
  mitigation,
  heading,
  className,
}: RecommendedActionsProps): ReactElement {
  // `<section>` maps to `region` only when it has an ACCESSIBLE NAME, which a
  // contained heading does not supply — so with `heading` omitted the tag
  // bought nothing and claimed to be a sectioning element. A heading-less card
  // body is a generic container, so render one.
  //
  // Named on neither path, deliberately: the legacy layout matches its sibling
  // `WhyThisFired` (`<section>` + `<h3>`, unnamed), and `aria-label`ing only
  // this one would put a lone landmark next to four unnamed AccentCards. That
  // is a folder-wide call, not one to make from inside a new component.
  const Wrapper = heading != null ? 'section' : 'div';
  return (
    <Wrapper
      data-testid="alert-recommended-actions"
      className={cn('min-w-0', className)}
    >
      {/* Heading styling matches the sibling legacy sections (WhyThisFired);
          inside an AccentCard the card supplies the title, so it is omitted. */}
      {heading != null && (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {heading}
        </h3>
      )}
      <p
        className={cn(
          'text-sm text-muted-foreground break-words',
          heading != null && 'mt-1'
        )}
      >
        {mitigation}
      </p>
    </Wrapper>
  );
}

RecommendedActions.displayName = 'RecommendedActions';
